import React, { useState } from "react";
import PropTypes from "prop-types";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, database } from "../../firebaseConfig";
import ProfileForm from "../Profile Form";
import LoadingSpinner from '../loader2';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaEnvelope, FaLock, FaGoogle } from 'react-icons/fa';

const MAX_DEVICES = 100;

const Login = ({ onLoginSuccess, setError, setUser, isProfileIncomplete, setIsProfileIncomplete }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(database, "users", user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const devices = userData.devices || [];

        if (devices.length >= MAX_DEVICES) {
          setLoginError({ message: "You have reached the maximum number of allowed devices." });
          setLoading(false);
          return;
        }

        const currentDevice = navigator.userAgent;
        const updatedDevices = [...devices, currentDevice];

        await updateDoc(userRef, {
          devices: updatedDevices,
          lastActiveAt: serverTimestamp()
        });

        setIsProfileIncomplete(!userData.profileCompleted);
      } else {
        await setDoc(userRef, {
          email: user.email,
          devices: [navigator.userAgent],
          profileCompleted: false,
          role: 'user',
          createdAt: serverTimestamp(),
          lastActiveAt: serverTimestamp()
        });
        setIsProfileIncomplete(true);
      }

      onLoginSuccess(user);
      setUser(user);
      setLoading(false);
    } catch (err) {
      console.error("Login failed:", err);
      setLoginError({ message: "Login failed. Please check your credentials." });
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setLoginError(null);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(database, "users", user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: user.email,
          devices: [navigator.userAgent],
          profileCompleted: false,
          role: 'user',
          createdAt: serverTimestamp(),
          lastActiveAt: serverTimestamp()
        });
        setIsProfileIncomplete(true);
      } else {
        setIsProfileIncomplete(!userDoc.data().profileCompleted);
        await updateDoc(userRef, {
          lastActiveAt: serverTimestamp()
        });
      }

      onLoginSuccess(user);
      setUser(user);
      setLoading(false);
    } catch (err) {
      console.error("Google login failed:", err);
      setLoginError({ message: "Google login failed. Please try again." });
      setLoading(false);
    }
  };

  return (
    <>
      {isProfileIncomplete && <ProfileForm user={auth.currentUser} setIsProfileIncomplete={setIsProfileIncomplete} />}

      {!loading ? (
        <div className="relative flex items-center justify-center min-h-screen bg-dark-primary overflow-hidden">
          {/* Animated background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-dark-primary to-pink-900/20" />

          {/* Floating orbs */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full filter blur-3xl"
              animate={{
                x: [0, 50, 0],
                y: [0, 30, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-600/20 rounded-full filter blur-3xl"
              animate={{
                x: [0, -50, 0],
                y: [0, -30, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          </div>

          {/* Floating particles */}
          <div className="absolute inset-0">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-purple-500/30"
                style={{
                  width: `${Math.random() * 8 + 4}px`,
                  height: `${Math.random() * 8 + 4}px`,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -100, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: Math.random() * 5 + 5,
                  repeat: Infinity,
                  delay: Math.random() * 5,
                  ease: 'easeInOut'
                }}
              />
            ))}
          </div>

          {/* Login card */}
          <motion.div
            className="relative w-full max-w-md p-8 space-y-6 bg-dark-secondary/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl z-10 mx-4"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Logo and Brand */}
            <div className="text-center">
              <motion.div
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 via-violet-600 to-pink-500 rounded-2xl shadow-lg mb-4"
                whileHover={{ scale: 1.05, rotate: 5 }}
              >
                <FaGraduationCap className="text-white text-2xl" />
              </motion.div>
              <h2 className="text-2xl font-bold text-white">
                Welcome to <span className="text-purple-400">PREPIFY</span>
              </h2>
              <p className="mt-2 text-sm text-gray-400">
                Sign in to continue your preparation journey
              </p>
            </div>

            {loginError && (
              <motion.div
                className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-start gap-3"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm">{loginError.message}</p>
              </motion.div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-500" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full pl-11 pr-4 py-3 bg-dark-tertiary/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaLock className="text-gray-500" />
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full pl-11 pr-4 py-3 bg-dark-tertiary/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-600 bg-dark-tertiary text-purple-600 focus:ring-purple-500/50 focus:ring-2"
                  />
                  <span className="ml-2">Remember me</span>
                </label>
                <a href="#" className="text-purple-400 hover:text-purple-300 transition-colors">
                  Forgot password?
                </a>
              </div>

              <motion.button
                type="submit"
                className="w-full py-3.5 px-4 bg-gradient-to-r from-purple-600 via-violet-600 to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-purple-500/25 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Sign In
              </motion.button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-dark-secondary text-gray-500">Or continue with</span>
                </div>
              </div>

              <motion.button
                onClick={handleGoogleLogin}
                type="button"
                className="w-full flex justify-center items-center gap-3 py-3.5 px-4 border border-white/10 rounded-xl text-gray-300 bg-dark-tertiary/30 hover:bg-dark-tertiary/50 hover:border-white/20 transition-all duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaGoogle className="text-lg" />
                <span className="font-medium">Sign in with Google</span>
              </motion.button>
            </form>

            <p className="text-center text-sm text-gray-500">
              Don't have an account?{" "}
              <a href="#" className="text-purple-400 hover:text-purple-300 font-medium transition-colors">
                Sign up for free
              </a>
            </p>
          </motion.div>
        </div>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
};

Login.propTypes = {
  onLoginSuccess: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  isProfileIncomplete: PropTypes.bool.isRequired,
  setIsProfileIncomplete: PropTypes.func.isRequired,
};

export default Login;